/**
 * ViewModel Mappers for Extras - Phase 2, Step 2.3
 *
 * These mappers transform domain data (from Model) into presentation-ready state (for View).
 * They implement presentation logic and business rules for UI display.
 */

import type { PurchaseInfo, Bundle } from '../model/extras.domain';
import type {
  PurchaseDisplayState,
  BundleDisplayData,
} from './extras.viewModel.types';

/**
 * Maps bundle status to UI color for status indicators.
 *
 * Business rule:
 * - published → green (success)
 * - archived → red (error)
 * - draft → yellow (warning)
 */
const getStatusColor = (p: {
  status: 'draft' | 'published' | 'archived';
}): 'success' | 'error' | 'warning' => {
  switch (p.status) {
    case 'published':
      return 'success';
    case 'archived':
      return 'error';
    case 'draft':
      return 'warning';
  }
};

/**
 * Transforms domain Bundle to presentation BundleDisplayData.
 * Adds UI-specific properties like statusColor.
 */
const toBundleDisplayData = (p: { bundle: Bundle }): BundleDisplayData => ({
  id: p.bundle.id,
  title: p.bundle.title,
  status: p.bundle.status,
  statusColor: getStatusColor({ status: p.bundle.status }),
});

/**
 * Transforms domain PurchaseInfo into presentation PurchaseDisplayState.
 *
 * Business rules:
 * - not-purchasable (ContentTab, ContentImage) → not-displayed
 * - Default access → "Available with subscription" label
 * - BundlesOnly → show bundle list only
 * - SubscriptionAndBundles → show label + bundle list
 *
 * @param p.purchaseInfo - Domain purchase information from Model
 * @returns Presentation-ready display state for View to render
 */
export const toPurchaseDisplayState = (p: {
  purchaseInfo: PurchaseInfo;
}): PurchaseDisplayState => {
  if (p.purchaseInfo.type === 'not-purchasable') {
    return { type: 'not-displayed' };
  }

  const { accessMethod, bundles } = p.purchaseInfo;

  // Default access: subscription only
  if (accessMethod === 'Default') {
    return {
      type: 'default-access',
      label: 'Any Purchase',
    };
  }

  // Transform bundles to display data
  const bundleDisplayData = bundles.map((bundle) =>
    toBundleDisplayData({ bundle }),
  );

  // BundlesOnly: show bundles only
  if (accessMethod === 'BundlesOnly') {
    return {
      type: 'bundles-only',
      bundles: bundleDisplayData,
    };
  }

  // SubscriptionAndBundles: show label + bundles
  return {
    type: 'subscription-and-bundles',
    label: 'Subscription',
    bundles: bundleDisplayData,
  };
};
